import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, useSearchParams, useParams } from "react-router-dom";
import { readPodCode, rememberPodEntry } from "@/lib/podEntry";
import { HelmetProvider } from "react-helmet-async";
import { useAuth } from "./contexts/AuthContext";
import { LoginForm } from "./components/auth/LoginForm";
import { ClientMagicLinkLogin } from "./components/auth/ClientMagicLinkLogin";
import { BrandLogo } from "./components/BrandLogo";
import { BrandLoadingSplash } from "./components/ui/BrandLoadingSplash";
import { OfflineIndicator } from "./components/ui/OfflineIndicator";
import { PushNotificationPrompt } from "./components/ui/PushNotificationPrompt";
import { PWAUpdatePrompt } from "./components/ui/PWAUpdatePrompt";
import { PWAInstallBanner } from "./components/ui/PWAInstallBanner";
import { PlatformGuide } from "./components/ui/PlatformGuide";

// Lazy-loaded page components for route-based code splitting
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Login = lazy(() => import("./pages/Login"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ProfileOnboarding = lazy(() => import("./pages/ProfileOnboarding"));
const ClientOnboarding = lazy(() => import("./pages/ClientOnboarding"));
const ClientRegistration = lazy(() => import("./pages/ClientRegistration"));
const ClientERP = lazy(() => import("./pages/ClientERP"));
const TrainerOnboarding = lazy(() => import("./pages/TrainerOnboarding"));
const QuestionnaireUpdate = lazy(() => import("./pages/QuestionnaireUpdate"));
const TrainerDashboard = lazy(() => import("./pages/TrainerDashboard"));
const TrainerMarketplace = lazy(() => import("./pages/TrainerMarketplace"));
const TrainerSwipePage = lazy(() => import("./pages/TrainerSwipePage"));
const TinderSwipePage = lazy(() => import("./pages/TinderSwipePage"));
const TrainerShortlistPage = lazy(() => import("./pages/TrainerShortlistPage"));
const TrainerERP = lazy(() => import("./pages/TrainerERP"));
const TrainerProfilePage = lazy(() => import("./pages/TrainerProfilePage"));
const AutomationUpgrade = lazy(() => import("./pages/AutomationUpgrade"));
const TrainerProfileDetailPage = lazy(() => import("./pages/TrainerProfileDetailPage"));
const PublicTrainerProfile = lazy(() => import("./pages/PublicTrainerProfile"));
const PublicGymProfile = lazy(() => import("./pages/PublicGymProfile"));
const PublicPost = lazy(() => import("./pages/PublicPost"));
const PublicUserProfile = lazy(() => import("./pages/PublicUserProfile"));
const TrainerLogin = lazy(() => import("./pages/TrainerLogin"));
const ClientLogin = lazy(() => import("./pages/ClientLogin"));
const ClientSignup = lazy(() => import("./pages/ClientSignup"));
const JoinNetwork = lazy(() => import("./pages/JoinNetwork"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
const Messages = lazy(() => import("./pages/Messages"));
const QuestionnaireTest = lazy(() => import("./pages/QuestionnaireTest"));
const DesignLab = lazy(() => import("./pages/DesignLab"));
const AdminQualifications = lazy(() => import("./pages/AdminQualifications"));
const AffiliateAdmin = lazy(() => import("./pages/AffiliateAdmin"));
const TrainerConnectionPage = lazy(() => import("./pages/TrainerConnectionPage"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const SubscribeSuccess = lazy(() => import("./pages/SubscribeSuccess"));
const PaymentSummary = lazy(() => import("./pages/PaymentSummary"));
const UpgradePaymentSummary = lazy(() => import("./pages/UpgradePaymentSummary"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentFailed = lazy(() => import("./pages/PaymentFailed"));
const ClientPaymentHistory = lazy(() => import("./pages/ClientPaymentHistory"));
const TrainerMatchingResults = lazy(() => import("./pages/TrainerMatchingResults"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TrainerTerms = lazy(() => import("./pages/TrainerTerms"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const AcceptableUse = lazy(() => import("./pages/AcceptableUse"));
const MyConsultation = lazy(() => import("./pages/MyConsultation"));
const Install = lazy(() => import("./pages/Install"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const VerifyMember = lazy(() => import("./pages/VerifyMember"));
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail"));
const CoachGateway = lazy(() => import("./pages/CoachGateway"));
const GymGateway = lazy(() => import("./pages/GymGateway"));
const GymOwnerSignup = lazy(() => import("./pages/GymOwnerSignup"));
const GymOwnerDashboard = lazy(() => import("./pages/GymOwnerDashboard"));

// Scan & BIA pages — direct preview routes only, not linked from navigation
const ScansHome = lazy(() => import("./pages/ScansHome"));
const ScanFullReport = lazy(() => import("./pages/ScanFullReport"));
const ScanAnalysis = lazy(() => import("./pages/ScanAnalysis"));
const PodLanding  = lazy(() => import("./pages/PodLanding"));
const PodSignup   = lazy(() => import("./pages/PodSignup"));
const ScanActivate = lazy(() => import("./pages/ScanActivate"));
const ScanActivateVerify = lazy(() => import("./pages/ScanActivateVerify"));
const ScanProfile = lazy(() => import("./pages/ScanProfile"));
const ScanReady = lazy(() => import("./pages/ScanReady"));
const ScanStart = lazy(() => import("./pages/ScanStart"));
const ScanSetup = lazy(() => import("./pages/ScanSetup"));
const ScanHub = lazy(() => import("./pages/ScanHub"));
const ScanAiReport = lazy(() => import("./pages/ScanAiReport"));
const TrendsPage = lazy(() => import("./pages/TrendsPage"));
const HealthJourney = lazy(() => import("./pages/HealthJourney"));
const ScanMatchIntro = lazy(() => import("./pages/ScanMatchIntro"));
const ScanMatchQuestions = lazy(() => import("./pages/ScanMatchQuestions"));
const ScanMatchDeck = lazy(() => import("./pages/ScanMatchDeck"));
const ScanMatchSaved = lazy(() => import("./pages/ScanMatchSaved"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const HealthGuruPage = lazy(() => import("./pages/HealthGuruPage"));
const FoodDiaryPage = lazy(() => import("./pages/FoodDiaryPage"));
const MyTrainingPage = lazy(() => import("./pages/MyTrainingPage"));
const RewardDetailsPage = lazy(() => import("./pages/RewardDetailsPage"));

const RouteLoadingFallback = () => <BrandLoadingSplash />;

// Silently fixes any double-slash URL (e.g. //scan-match/questions → /scan-match/questions)
// by replacing the current history entry before React Router tries to match it.
function PathNormalizer() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.pathname.includes('//')) {
      const clean = location.pathname.replace(/\/+/g, '/');
      navigate(clean + location.search, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);
  return null;
}

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <BrandLoadingSplash />;
  }
  
  if (!user) {
    const pathname = location.pathname;
    
    if (pathname.startsWith("/my-consultation")) {
      return <ClientMagicLinkLogin />;
    }
    
    if (pathname.startsWith("/client")) {
      return <ClientLogin />;
    }

    // Trainer ERP gets the trainer-specific form
    if (pathname.startsWith("/trainer-erp")) {
      return <LoginForm />;
    }

    // All other protected routes (invoice, payment, scans, etc.):
    // redirect to the proper login page and return here after auth
    return <Navigate to={`/login?redirect=${encodeURIComponent(pathname + location.search)}`} replace />;
  }
  
  return <>{children}</>;
};

// A pod's own QR code / link. Routing is unchanged (a signed-in member goes to Start scan; anyone else is sent to log in
// and returns here). The only addition: when the visitor is not signed in, remember which pod they came for, so the shared
// screens they pass through next can keep that gym in view.
const ScanAtRoute = () => {
  const { user, loading } = useAuth();
  const { code } = useParams();
  const navigate = useNavigate();
  const pod = readPodCode(code);
  // Someone who is not signed in and opens a scanner's QR link is, in nearly every case, new: send them to sign-up
  // with that scanner remembered, so after signing up they continue straight to the scan flow for it. Existing
  // members use the "Log in" link on that page, which also returns them here.
  const signUpFirst = !loading && !user && !!pod;
  useEffect(() => {
    if (!signUpFirst || !pod) return;
    rememberPodEntry(pod);
    navigate('/pod/signup', { replace: true });
  }, [signUpFirst, pod, navigate]);
  if (signUpFirst) return null;
  return <ProtectedRoute><ScanStart /></ProtectedRoute>;
};

const ClientRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/scans', { replace: true });
  }, [navigate]);
  return null;
};

const SettingsRedirect = () => {
  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const targetPath = `/trainer-erp?tab=settings${queryString ? `&${queryString}` : ''}`;
  return <Navigate to={targetPath} replace />;
};

const LoginRedirect = () => <Navigate to="/login" replace />;

// Preload the most-visited routes during browser idle time so users don't
// see the pulsing splash on first navigation after login.
if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
  (window as any).requestIdleCallback(() => {
    import('./pages/ClientERP');
    import('./pages/ScansHome');
    import('./pages/TrainerERP');
    import('./pages/Login');
    import('./pages/ScanActivate');
    import('./pages/AuthCallback');
  });
}

const App = () => {
  console.log('🔷 App component rendering');

  return (
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <OfflineIndicator />
        <PushNotificationPrompt />
        <PWAUpdatePrompt />
        <BrowserRouter>
          {/* <PlatformGuide /> */}
          <PWAInstallBanner />
          <Suspense fallback={<RouteLoadingFallback />}>
            <PathNormalizer />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/onboarding/profile" element={<ProfileOnboarding />} />
              <Route path="/client-onboarding" element={<ClientOnboarding />} />
              <Route path="/client-registration" element={<ClientRegistration />} />
              <Route path="/trainer-onboarding" element={<TrainerOnboarding />} />
              <Route path="/upgrade-automation" element={<AutomationUpgrade />} />
              <Route path="/trainer-login" element={<LoginRedirect />} />
              <Route path="/client-login" element={<LoginRedirect />} />
              <Route path="/client-signup" element={<ClientSignup />} />
              <Route path="/join" element={<JoinNetwork />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/questionnaire-update" element={<QuestionnaireUpdate />} />
              <Route path="/client" element={
                <ProtectedRoute>
                  <ClientRedirect />
                </ProtectedRoute>
              } />
              <Route path="/client-dashboard" element={
                <ProtectedRoute>
                  <ClientDashboard />
                </ProtectedRoute>
              } />
              <Route path="/client-erp" element={
                <ProtectedRoute>
                  <ClientERP />
                </ProtectedRoute>
              } />
              <Route path="/trainer" element={<TrainerDashboard />} />
              <Route path="/trainer-profile" element={<TrainerProfilePage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/trainer-erp" element={
                <ProtectedRoute>
                  <TrainerERP />
                </ProtectedRoute>
              } />
              <Route path="/marketplace" element={<TrainerMarketplace />} />
              <Route path="/discover" element={
                <ProtectedRoute>
                  <TrainerSwipePage />
                </ProtectedRoute>
              } />
              <Route path="/trainer-connection" element={
                <ProtectedRoute>
                  <TrainerConnectionPage />
                </ProtectedRoute>
              } />
              <Route path="/find-coach" element={
                <ProtectedRoute>
                  <CoachGateway />
                </ProtectedRoute>
              } />
              <Route path="/find-gym" element={
                <ProtectedRoute>
                  <GymGateway />
                </ProtectedRoute>
              } />
              <Route path="/gym-owner-signup" element={<GymOwnerSignup />} />
              <Route path="/gym-owner" element={
                <ProtectedRoute>
                  <GymOwnerDashboard />
                </ProtectedRoute>
              } />
              <Route path="/trainer/:trainerId" element={<TrainerProfileDetailPage />} />
              <Route path="/t/:trainerCode" element={<PublicTrainerProfile />} />
              <Route path="/gym/:gymSlug" element={<PublicGymProfile />} />
              <Route path="/t/:trainerCode/post/:postId" element={<PublicPost />} />
              <Route path="/u/:userId" element={<PublicUserProfile />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/questionnaire-test" element={<QuestionnaireTest />} />
              <Route path="/design-lab" element={<DesignLab />} />
              <Route path="/admin/affiliates" element={<ProtectedRoute><AffiliateAdmin /></ProtectedRoute>} />
              <Route path="/admin/qualifications" element={<AdminQualifications />} />
              <Route path="/subscribe" element={<Subscribe />} />
              <Route path="/subscribe/success" element={<SubscribeSuccess />} />
              <Route path="/payment-summary/:packageId" element={
                <ProtectedRoute>
                  <PaymentSummary />
                </ProtectedRoute>
              } />
              <Route path="/upgrade-payment/:invoiceId" element={
                <ProtectedRoute>
                  <UpgradePaymentSummary />
                </ProtectedRoute>
              } />
              <Route path="/payment-success" element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              } />
              <Route path="/payment-failed" element={
                <ProtectedRoute>
                  <PaymentFailed />
                </ProtectedRoute>
              } />
              <Route path="/payment-history" element={
                <ProtectedRoute>
                  <ClientPaymentHistory />
                </ProtectedRoute>
              } />
              <Route path="/trainer-matches" element={
                <ProtectedRoute>
                  <TrainerMatchingResults />
                </ProtectedRoute>
              } />
              <Route path="/trainer-marketplace" element={<ProtectedRoute><TrainerMarketplace /></ProtectedRoute>} />
              <Route path="/trainer-swipe" element={<ProtectedRoute><TrainerSwipePage /></ProtectedRoute>} />
              <Route path="/meet-your-match" element={<ProtectedRoute><TinderSwipePage /></ProtectedRoute>} />
              <Route path="/shortlist" element={<ProtectedRoute><TrainerShortlistPage /></ProtectedRoute>} />
              <Route path="/settings" element={<SettingsRedirect />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/trainer-terms" element={<TrainerTerms />} />
              <Route path="/refunds" element={<RefundPolicy />} />
              <Route path="/acceptable-use" element={<AcceptableUse />} />
              <Route path="/my-consultation" element={
                <ProtectedRoute>
                  <MyConsultation />
                </ProtectedRoute>
              } />
              <Route path="/install" element={<Install />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/verify-member" element={<VerifyMember />} />
              <Route path="/invoice/:id" element={
                <ProtectedRoute>
                  <InvoiceDetail />
                </ProtectedRoute>
              } />
              {/* Scan & BIA routes */}
              <Route path="/scans-home" element={<ProtectedRoute><ScansHome /></ProtectedRoute>} />
              <Route path="/scan-report" element={<ProtectedRoute><ScanFullReport /></ProtectedRoute>} />
              <Route path="/scan-analysis" element={<ProtectedRoute><ScanAnalysis /></ProtectedRoute>} />
              <Route path="/pod" element={<PodLanding />} />
              <Route path="/pod/signup" element={<PodSignup />} />
              <Route path="/scan-activate" element={<ProtectedRoute><ScanActivate /></ProtectedRoute>} />
              <Route path="/scan-activate/verify" element={<ProtectedRoute><ScanActivateVerify /></ProtectedRoute>} />
              <Route path="/scan-profile" element={<ProtectedRoute><ScanProfile /></ProtectedRoute>} />
              <Route path="/scan-setup" element={<ProtectedRoute><ScanSetup /></ProtectedRoute>} />
              <Route path="/scan-ready" element={<ProtectedRoute><ScanReady /></ProtectedRoute>} />
              <Route path="/scan-start" element={<ProtectedRoute><ScanStart /></ProtectedRoute>} />
              <Route path="/scan-at/:code" element={<ScanAtRoute />} />
              <Route path="/scans" element={<ProtectedRoute><ScanHub /></ProtectedRoute>} />
              <Route path="/scan-ai-report" element={<ProtectedRoute><ScanAiReport /></ProtectedRoute>} />
              <Route path="/trends" element={<ProtectedRoute><TrendsPage /></ProtectedRoute>} />
              <Route path="/health-journey" element={<ProtectedRoute><HealthJourney /></ProtectedRoute>} />
              <Route path="/health-journey/food" element={<ProtectedRoute><FoodDiaryPage /></ProtectedRoute>} />
              <Route path="/health-journey/training" element={<ProtectedRoute><MyTrainingPage /></ProtectedRoute>} />
              <Route path="/health-guru" element={<ProtectedRoute><HealthGuruPage /></ProtectedRoute>} />
              <Route path="/rewards/:rewardId" element={<ProtectedRoute><RewardDetailsPage /></ProtectedRoute>} />
              <Route path="/scan-match/intro" element={<ProtectedRoute><ScanMatchIntro /></ProtectedRoute>} />
              <Route path="/scan-match/questions" element={<ProtectedRoute><ScanMatchQuestions /></ProtectedRoute>} />
              <Route path="/scan-match/deck" element={<ProtectedRoute><ScanMatchDeck /></ProtectedRoute>} />
              <Route path="/scan-match/saved" element={<ProtectedRoute><ScanMatchSaved /></ProtectedRoute>} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  );
};

export default App;
